import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, Button, Collapse, Link, Paper, Typography } from '@mui/material';
import LocationOn from '@mui/icons-material/LocationOn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import clsx from 'clsx';
import { DESKTOP_COLORS } from 'src/consts';
import { useResponsive } from 'src/hooks/useResponsive';
import { makeRouteScenario } from 'src/utils/route';
import { OrderScenario } from 'components/order/model/Order';
import { addOpacity } from 'lib/helpers';
import { isDefined } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { ApplicationState } from 'store/store';
import { getWorkingHoursSummary, resolveTimezone } from './openingHoursHelpers';
import { Location } from './model/Location';
import { LocationWorkingHours } from './LocationWorkingHours';
import { isHIBSV4, isYoungs } from 'lib/useGAHelpers';

const PREFIX = 'LocationCard';

const classes = {
    address: `${PREFIX}address`,
    phone: `${PREFIX}phone`,
    locationPaper: `${PREFIX}locationPaper`,
    locationTitle: `${PREFIX}locationTitle`,
    locationImg: `${PREFIX}locationImg`,
    locationIcon: `${PREFIX}locationIcon`,
    seeMore: `${PREFIX}seeMore`,
    seeMoreIcon: `${PREFIX}seeMoreIcon`,
    workingHoursToday: `${PREFIX}workingHoursToday`,
    orderCta: `${PREFIX}orderCta`,
    fullAddress: `${PREFIX}fullAddress`,
    workingHours: `${PREFIX}workingHours`
};

const getLocationImageSize = (isDesktop: boolean) => {
    if (isDesktop) {
        return '100px';
    }
    return '96px';
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    [`& .${classes.address}`]: {
        ...(isDesktop
            ? {
                  color: DESKTOP_COLORS.TEXT_SECONDARY,
                  fontSize: theme.spacing(1.75),
                  fontWeight: theme.typography.fontWeightMedium
              }
            : {})
    },

    [`& .${classes.phone}`]: {
        ...(isDesktop
            ? {
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {
                  color: theme.palette.text.primary
              })
    },

    [`& .${classes.locationPaper}`]: {
        height: '100%',
        ...(isDesktop
            ? {
                  boxShadow: `3px 3px 7px 6px ${addOpacity(theme.palette.common.black, 0.07)}`,
                  backgroundColor: theme.palette.common.white
              }
            : {
                  border: `1px solid ${theme.palette.divider}`
              })
    },

    [`& .${classes.locationTitle}`]: {
        fontWeight: theme.typography.fontWeightBold,
        ...(isDesktop
            ? {
                  fontSize: theme.spacing(2.5),
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {})
    },

    [`& .${classes.locationImg}`]: {
        width: getLocationImageSize(isDesktop),
        height: getLocationImageSize(isDesktop),
        borderRadius: theme.shape.borderRadius,
        marginLeft: 'auto',
        flexShrink: 0,
        objectFit: 'cover'
    },

    [`& .${classes.locationIcon}`]: {
        marginRight: theme.spacing(0.5),
        color: theme.palette.common.black,
        ...(isDesktop
            ? {
                  width: '16px'
              }
            : {})
    },

    [`& .${classes.seeMore}`]: {
        textTransform: 'initial',
        padding: 0,
        ...(isDesktop
            ? {
                  fontSize: theme.spacing(1.75),
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {
                  fontSize: theme.typography.body1.fontSize
              })
    },

    [`& .${classes.seeMoreIcon}`]: {
        transition: 'transform .1s',
        '&.active': {
            transform: 'rotate(180deg)'
        }
    },

    [`& .${classes.workingHoursToday}`]: {
        ...(isDesktop
            ? {
                  fontSize: theme.spacing(1.75),
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {})
    },

    [`& .${classes.orderCta}`]: {
        height: '100%'
    },

    [`& .${classes.fullAddress}`]: {
        ...(isDesktop
            ? {
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {})
    },

    [`& .${classes.workingHours}`]: {
        width: 'max-content',
        fontSize: theme.typography.body1.fontSize,
        borderSpacing: 0,
        marginLeft: theme.spacing(-1),
        ...(isDesktop
            ? {
                  color: DESKTOP_COLORS.TEXT_PRIMARY
              }
            : {}),
        '& tr>td': {
            padding: theme.spacing(0.5, 1, 0.5, 1),
            '&:first-child': {
                borderTopLeftRadius: theme.shape.borderRadius,
                borderBottomLeftRadius: theme.shape.borderRadius
            },
            '&:last-child': {
                borderTopRightRadius: theme.shape.borderRadius,
                borderBottomRightRadius: theme.shape.borderRadius
            }
        },
        '& tr.active': {
            backgroundColor: theme.palette.divider,
            fontWeight: theme.typography.fontWeightBold
        }
    }
}));

interface IProps {
    location: Location;
}

export const LocationCard = ({ location }: IProps) => {
    const { isDesktop, isMobile } = useResponsive();
    const { merchantId } = useParams<LocationRouteParams>();

    const { t } = useTranslation();
    const { push } = useLocalHistory();
    const [isMoreOpen, setIsMoreOpen] = React.useState(false);

    const distanceUnit = useSelector(
        (state: ApplicationState) => state.settings.settings?.region.distanceUnit
    );
    const regionTimezone = useSelector((state: ApplicationState) => state.settings.settings?.region.timezone);

    let { distance } = location;
    if (distanceUnit === 'mi') {
        distance = isDefined(distance) ? distance * 0.621371 : undefined;
    }
    const workingHours = React.useMemo(() => {
        if (!location.openingHours) {
            return '';
        }
        const timezone = resolveTimezone(location.timezone, regionTimezone);
        const summary = getWorkingHoursSummary(location.openingHours, timezone);
        if ((isYoungs(merchantId) || isHIBSV4(merchantId)) && summary.isOpenAllDay) {
            return '';
        }
        return summary.isOpenAllDay ? t('LOCATIONS_LIST_OPEN_ALL_DAY') : summary.label;
    }, [location.openingHours, location.timezone, regionTimezone, t, merchantId]);

    const toggleShowMore = React.useCallback(() => setIsMoreOpen(value => !value), []);

    const addressLine = React.useMemo(
        () =>
            !!location
                ? [
                      location.address.address,
                      location.address.town,
                      location.address.country,
                      location.address.postcode
                  ]
                      .filter(item => !!item)
                      .join(', ')
                : '',
        [location]
    );

    const handleClick = React.useCallback(() => {
        push(
            ROUTES.MENU,
            { locationId: location._id, scenario: makeRouteScenario(OrderScenario.PREORDER) },
            undefined,
            {
                preoderEntry: 'locations'
            }
        );
    }, [location._id, push]);

    return (
        <StyledBox key={location._id} isDesktop={isDesktop}>
            <Box height="100%" marginTop={1}>
                <Paper elevation={0} className={classes.locationPaper}>
                    <Box padding={1.5}>
                        <Box marginBottom={2} display="flex">
                            <div>
                                <Typography className={classes.locationTitle} variant="h6">
                                    {location.title}
                                </Typography>
                                <Typography className={classes.address} variant="body2">
                                    {location.address.summary}
                                </Typography>
                                {!!distance && (
                                    <Typography className={classes.address} variant="caption">
                                        <LocationOn className={classes.locationIcon} fontSize="inherit" />
                                        {`${Math.floor(distance).toLocaleString()}${distanceUnit || 'km'}`}
                                    </Typography>
                                )}
                            </div>
                            {location.listingImageUrl ? (
                                <img
                                    className={classes.locationImg}
                                    src={location.listingImageUrl}
                                    alt={location.title}
                                />
                            ) : (
                                <div className={classes.locationImg} />
                            )}
                        </Box>

                        <Box marginTop={1.5} display="flex">
                            <Box>
                                <Typography className={classes.workingHoursToday}>{workingHours}</Typography>
                                <Button
                                    className={classes.seeMore}
                                    onClick={toggleShowMore}
                                    color="inherit"
                                    variant="text"
                                >
                                    <b>{t('LOCATION_DETAILS_SHOW_MORE')}</b>
                                    <KeyboardArrowDownIcon
                                        className={clsx(classes.seeMoreIcon, { active: isMoreOpen })}
                                    />
                                </Button>
                            </Box>
                            {!isMobile && (
                                <Box marginLeft="auto" maxWidth="100px" width="100%">
                                    <Button
                                        onClick={handleClick}
                                        variant="contained"
                                        fullWidth
                                        color="primary"
                                        className={classes.orderCta}
                                    >
                                        {t('LOCATION_START_COLLECTION_CTA')}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <Collapse in={isMoreOpen}>
                            <Box marginTop={1}>
                                <Typography className={classes.fullAddress}>
                                    <Trans
                                        i18nKey="LOCATION_ADDRESS"
                                        components={[<b key="address" />]}
                                        values={{ address: addressLine }}
                                    />
                                </Typography>
                                {location.contacts?.phone && (
                                    <Link className={classes.phone} href={`tel:${location.contacts.phone}`}>
                                        <Typography>
                                            <b>
                                                {t('LOCATION_CONTACT_PHONE', {
                                                    phone: location.contacts.phone
                                                })}
                                            </b>
                                        </Typography>
                                    </Link>
                                )}
                                {location.openingHours && (
                                    <Box marginTop={1}>
                                        <LocationWorkingHours openingHours={location.openingHours} />
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                        {isMobile && (
                            <Box marginTop={1}>
                                <Button onClick={handleClick} variant="contained" fullWidth color="primary">
                                    {t('LOCATION_START_COLLECTION_CTA')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </StyledBox>
    );
};
